import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Lock, Loader2, Eye, EyeOff } from "lucide-react";

export default function ChangePasswordCard({ accent = "green" }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (form.new_password !== form.confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated", { description: "Use your new password next time you sign in." });
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error("Could not update password", { description: e?.response?.data?.detail || "Try again." });
    } finally { setLoading(false); }
  };

  return (
    <div className="card-luxe p-6" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-5">
        <Lock className="w-5 h-5 text-[#0B5D3B]" />
        <h3 className="font-serif text-xl">Security · Change password</h3>
        <span className="ml-auto text-xs text-[#7A8A81]">Rotate regularly</span>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="change-password-form">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7A8A81] mb-1.5 block">Current</label>
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              required
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              className="w-full px-4 py-3 pr-10 rounded-xl border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none text-sm"
              data-testid="cp-current"
            />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7A8A81]" aria-label="Toggle password visibility">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7A8A81] mb-1.5 block">New (min 8)</label>
          <input
            type={show ? "text" : "password"}
            required
            minLength={8}
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none text-sm"
            data-testid="cp-new"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7A8A81] mb-1.5 block">Confirm new</label>
          <input
            type={show ? "text" : "password"}
            required
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none text-sm"
            data-testid="cp-confirm"
          />
        </div>
        <div className="md:col-span-3">
          <button
            disabled={loading}
            className="btn-primary text-sm py-2.5 px-5 disabled:opacity-60"
            data-testid="cp-submit"
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</> : <><Lock className="w-4 h-4 mr-2" /> Update password</>}
          </button>
        </div>
      </form>
    </div>
  );
}
